import { UrlState } from '../types/UrlState'
import { ActionTree } from 'vuex';
import * as types from './mutation-types'
// you can use this storage if you want to enable offline capabilities
import { cacheStorage } from '../'
import queryString from 'query-string'
import config from 'config'
import { processMultipleDynamicRoutes, normalizeUrlPath, parametrizeRouteData } from '../helpers'
import { removeStoreCodeFromRoute } from '@vue-storefront/core/lib/multistore'
import storeCodeFromRoute from '@vue-storefront/core/lib/storeCodeFromRoute'
import rootStore from '@vue-storefront/core/store'
import { Logger } from "@vue-storefront/core/lib/logger";
import { lookupProduct } from '@vue-storefront/core/lib/catalogSearch'

// it's a good practice for all actions to return Promises with effect of their execution
export const actions: ActionTree<UrlState, any> = {
  // if you want to use cache in your module you can load cached data like this
  async registerMapping ({ commit }, { url, routeData }: { url: string, routeData: any}) {
    commit(types.REGISTER_MAPPING, { url, routeData })
    try {
      await cacheStorage.setItem(normalizeUrlPath(url), routeData, null, config.seo.disableUrlRoutesPersistentCache)
    } catch (err) {
      if (
        err.name === 'QuotaExceededError' ||
        err.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      ) { // quota exceeded error
        cacheStorage.clear() // clear the url cache if quota has been exceeded
      }
    }
    return routeData
  },
  /**
   * Register dynamic vue-router routes
   */
  async registerDynamicRoutes ({ state, dispatch }) {
    if (state.dispatcherMap) {
      processMultipleDynamicRoutes(state.dispatcherMap) // check if we're to add routes to vue router
      const registrationQueue = []
      for (const [url, routeData] of Object.entries(state.dispatcherMap)) {
        registrationQueue.push(dispatch('registerMapping', { url, routeData }))
      }
      Promise.all(registrationQueue)
    }
  },
  mapUrl ({ state, dispatch }, { url, query }: { url: string, query: string}) {
    const parsedQuery = typeof query === 'string' ? queryString.parse(query) : query
    const storeCodeInPath = storeCodeFromRoute(url)
    url = normalizeUrlPath(url)

    return new Promise((resolve, reject) => {
      if (state.dispatcherMap[url]) {
        return resolve(parametrizeRouteData(state.dispatcherMap[url], query, storeCodeInPath))
      }
      cacheStorage.getItem(url).then(routeData => {
        if (routeData !== null) {
          return resolve(parametrizeRouteData(routeData, query, storeCodeInPath))
        } else {
          dispatch('mappingFallback', { url, params: parsedQuery }).then((routeData) => {
            // A 404 can be a transient lookup failure - don't pin it for the session.
            if (routeData.name !== '404') {
              dispatch('registerMapping', { url, routeData })
            }
            resolve(parametrizeRouteData(routeData, query, storeCodeInPath))
          }).catch(reject)
        }
      }).catch(reject)
    })
  },

  /**
   * Router mapping fallback - get the proper URL from API
   * This method could be overriden in custom module to provide custom URL mapping logic
   */
  async mappingFallback ({ getters, dispatch }, { url, params }: { url: string, params: any}) {
    url = (removeStoreCodeFromRoute(url) as string)
    if (!rootStore.getters['locations/getLocations'].length) {
      await rootStore.dispatch('locations/loadLocations')
    }
    let parts = url.split('/') //0 is location, 1 is partner, 2 is final part like product or category
    let location = rootStore.getters['locations/getLocations'].find(l => l.code === parts[0])
    let localActive = rootStore.getters['locations/getActiveLocation']
    if (location && !localActive && location !== localActive) {
      await rootStore.dispatch('locations/setCurrentLocation', {code: location.code})
    }
    if (location && parts.length === 1) {
      return {
        name: 'location',
        params: {
          locationCode: location.code
        }
      }
    }
    // Cheap, fetch-free check: if categories are already loaded (the common case -
    // this dispatcher is reached mid-navigation from an already-loaded category
    // page, e.g. toggling a tag) and parts[1] already matches one, this is a
    // category/tag URL, not a partner store URL - skip the partner lookup below
    // entirely rather than paying a guaranteed-404 `loadOnePartner` call for
    // every category/tag navigation. Falls through to the unchanged logic below
    // (which loads categories itself if still needed) whenever this doesn't hit.
    let earlyCategoryMatch = location && rootStore.getters['partner_categories/getCategories'].length &&
      rootStore.getters['partner_categories/getCategories'].find(c => c.code === parts[1])
    // Not the tag-filtered getPartners - a partner outside the selected tag must still resolve.
    const findPartner = () => rootStore.getters['partners/getPartnerByLocationAndByPartnerCode']({ partnerCode: parts[1], locationCode: parts[0] })
    if (location && !earlyCategoryMatch && !findPartner()) {
      try {
        await rootStore.dispatch('partners/loadOnePartner', { code: parts[1], locationCode: parts[0] })
      } catch (error) {
        Logger.error(error, 'sync')();
      }
    }
    let currentPartner = findPartner()
    let layout = 'default'
    if (currentPartner) {
      await rootStore.dispatch('partners/changeCurrentPartner', currentPartner.code)
      layout = currentPartner.display_mode === 'food' ? 'default' : 'shop'
    }
    if (location && currentPartner && parts.length === 2) {
      let name = currentPartner.display_mode === 'food' ? 'selected-store' : 'selected-shop'
      return {
        name,
        params: {
          locationCode: location.code,
          store: currentPartner.code
        },
        meta: {
          layout
        }
      }
    }
    // Partner sub-URLs (products, catalog categories) don't need partner categories.
    if (location && !currentPartner) {
      await rootStore.dispatch('partner_categories/ensureCategories', { locationCode: location.code })
    }
    let currentCategory = rootStore.getters['partner_categories/getCategories'].find(p => p.code === parts[1])
    if (location && currentCategory && parts.length === 2) {
      return {
        name: 'partners',
        params: {
          locationCode: location.code,
          listId: currentCategory.code
        }
      }
    }
    // Clean tag URL /{city}/{category}/{tag} (K2-2985), e.g. /ternopil/food/sushi -
    // replaces the old ?tags= query-string-only variant for cuisine tag pages.
    if (location && currentCategory && parts.length === 3) {
      await rootStore.dispatch('partner_tags/ensureTags', { categoryId: currentCategory.entity_id, locationCode: location.code })
      let currentTag = rootStore.getters['partner_tags/getTags'].find(t => t.code === parts[2])
      if (currentTag) {
        return {
          name: 'partners-tagged',
          params: {
            locationCode: location.code,
            listId: currentCategory.code,
            tags: currentTag.code
          }
        }
      }
    }
    if (currentPartner && parts.length === 5 && parts[2] === 'p') {
      let currentPartnerCode = currentPartner.code
      // Semantic sku lookup (core/lib/catalogSearch.ts's lookupProduct, via
      // product/single's own semanticFetch wiring) instead of a raw SearchQuery -
      // the old bare `sku.keyword` filter targeted the pre-K2-2976 per-partner
      // index, which no longer exists once the per-city model is live.
      const product = await dispatch('product/single', {
        options: { sku: parts[3] },
        partner: currentPartnerCode,
        setCurrentProduct: false,
        selectDefaultVariant: false
      }, { root: true }).catch(() => null)
      if (product) {
        return {
          name: product.type_id + '-product',
          params: {
            locationCode: parts[0],
            store: parts[1],
            slug: product.slug,
            parentSku: product.sku,
            childSku: params['childSku'] ? params['childSku'] : product.sku
          },
          meta: {
            layout
          }
        }
      }
    }
    // K2-2985: short product URL, /{location}/{partner}/{slug} - no /p/ marker.
    // product.slug always embeds the entity_id as a trailing suffix (e.g.
    // "inverno-315365", see formatProductLink/localizedRoute), so this is a
    // cheap, unambiguous id lookup rather than a fuzzy slug match - and, since
    // it's an exact id lookup, there's no realistic collision with a category's
    // own url_path (tried below as the fallback for anything this doesn't match).
    if (currentPartner && parts.length === 3) {
      const idMatch = parts[2].match(/-(\d+)$/)
      if (idMatch) {
        const productId = parseInt(idMatch[1], 10)
        const resp = await lookupProduct({ id: productId, partner: currentPartner.code }).catch(() => null)
        const product = resp && resp.items && resp.items[0]
        if (product) {
          return {
            name: product.type_id + '-product',
            params: {
              locationCode: parts[0],
              store: parts[1],
              slug: product.slug,
              parentSku: product.parentSku || product.sku,
              childSku: params['childSku'] ? params['childSku'] : product.sku
            },
            meta: {
              layout
            }
          }
        }
      }
    }
    if (parts.length > 2) {
      let locationCode = parts[0]
      let partnerCode = parts[1]
      let urlPath = parts.filter(i => i !== locationCode && i !== partnerCode).join('/')
      try {
        const category = await dispatch('category/single', { key: 'url_path', value: urlPath }, { root: true })
        if (category !== null) {
          return {
            name: 'category',
            params: {
              locationCode: parts[0],
              store: parts[1],
              slug: category.slug
            },
            meta: {
              layout
            }
          }
        }
      } catch (e) {}
    }

    return {
      name: '404'
    }
  }
}
